import { db } from '../../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { AuditService } from './AuditService';

export interface Inquiry {
  id: string;
  providerId: string;
  legalFirstName: string;
  legalLastName: string;
  preferredName?: string;
  dateOfBirth?: string;
  email: string;
  phone?: string;
  billingType: 'Insurance' | 'Self-pay';
  status: 'new' | 'intake_sent' | 'registered' | 'active' | 'archived';
  clientType: 'Adult' | 'Minor' | 'Couple';
  referredBy?: string;
  notes?: string;
  contact: {
    primaryName: string;
    primaryPhone: string;
    primaryEmail: string;
    secondaryName?: string;
    secondaryPhone?: string;
    secondaryEmail?: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

const COLLECTION = 'inquiries';

export class FirebaseInquiryService {

  /**
   * Create a new inquiry (prospective client)
   */
  static async createInquiry(
    data: Omit<Inquiry, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const docRef = doc(collection(db, COLLECTION));
    const now = Timestamp.now();

    await setDoc(docRef, {
      ...data,
      createdAt: now,
      updatedAt: now,
    });

    // Audit log
    try {
      await AuditService.log({
        action: 'inquiry_created',
        userId: data.providerId,
        userRole: 'provider',
        resourceType: 'inquiry',
        resourceId: docRef.id,
        resourceOwner: data.providerId,
        details: `Created inquiry for ${data.legalFirstName} ${data.legalLastName}`,
      });
    } catch (e) {
      // Non-blocking
    }

    return docRef.id;
  }

  /**
   * Get all inquiries for a provider
   */
  static async getInquiries(providerId: string): Promise<Inquiry[]> {
    const q = query(
      collection(db, COLLECTION),
      where('providerId', '==', providerId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        providerId: data.providerId,
        legalFirstName: data.legalFirstName,
        legalLastName: data.legalLastName,
        preferredName: data.preferredName,
        dateOfBirth: data.dateOfBirth,
        email: data.email,
        phone: data.phone,
        billingType: data.billingType,
        status: data.status,
        clientType: data.clientType,
        referredBy: data.referredBy,
        notes: data.notes,
        contact: data.contact || {
          primaryName: `${data.legalFirstName} ${data.legalLastName}`,
          primaryPhone: data.phone || '',
          primaryEmail: data.email,
        },
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
      } as Inquiry;
    });
  }

  /**
   * Get a single inquiry by ID
   */
  static async getInquiry(inquiryId: string): Promise<Inquiry | null> {
    const docSnap = await getDoc(doc(db, COLLECTION, inquiryId));
    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    return {
      id: docSnap.id,
      providerId: data.providerId,
      legalFirstName: data.legalFirstName,
      legalLastName: data.legalLastName,
      preferredName: data.preferredName,
      dateOfBirth: data.dateOfBirth,
      email: data.email,
      phone: data.phone,
      billingType: data.billingType,
      status: data.status,
      clientType: data.clientType,
      referredBy: data.referredBy,
      notes: data.notes,
      contact: data.contact || {
        primaryName: `${data.legalFirstName} ${data.legalLastName}`,
        primaryPhone: data.phone || '',
        primaryEmail: data.email,
      },
      createdAt: data.createdAt?.toDate?.() || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || new Date(),
    } as Inquiry;
  }

  /**
   * Update an inquiry
   */
  static async updateInquiry(
    inquiryId: string,
    updates: Partial<Omit<Inquiry, 'id' | 'createdAt'>>
  ): Promise<void> {
    await updateDoc(doc(db, COLLECTION, inquiryId), {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Archive an inquiry (soft delete)
   */
  static async archiveInquiry(inquiryId: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, inquiryId), {
      status: 'archived',
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Delete an inquiry (hard delete — inquiries are not PHI)
   */
  static async deleteInquiry(inquiryId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, inquiryId));
  }
}
